import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Image as ImageIcon,
  Video,
  DollarSign,
  Eye,
  Upload,
  X,
  Edit,
  Check,
  Sparkles,
  Tag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// File to Base64 converter with progress
const fileToBase64 = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Image Upload Component
const ImageUploader = ({ value, onChange, label, accept = "image/*" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError('');
    const isVideo = accept.includes('video');
    
    // Check file size (max 10MB for images, 100MB for videos)
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File is too large. Maximum size is ${isVideo ? '100MB' : '10MB'}`);
      return;
    }
    
    // For very large videos (>20MB), warn user
    if (isVideo && file.size > 20 * 1024 * 1024) {
      const proceed = window.confirm(
        `This video is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Large videos may take a while to process and could slow down page loading. Consider using a YouTube URL instead.\n\nProceed anyway?`
      );
      if (!proceed) return;
    }
    
    setUploading(true);
    setProgress(0);
    
    try {
      const base64 = await fileToBase64(file, setProgress);
      onChange(base64);
      setProgress(100);
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to process file. Try a smaller file or use a URL instead.');
    } finally {
      setUploading(false);
    }
  };
  
  const isVideo = accept.includes('video');
  const hasValue = value && (value.startsWith('data:') || value.startsWith('http'));
  
  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className={`border-2 border-dashed rounded-xl p-4 transition-colors ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-amber-400'}`}>
        {hasValue ? (
          <div className="relative">
            {isVideo ? (
              value.startsWith('data:video') || value.includes('.mp4') || value.includes('.webm') || value.includes('.mov') ? (
                <video src={value} controls className="w-full rounded-lg max-h-48 object-contain bg-black" />
              ) : value.includes('youtube') || value.includes('youtu.be') ? (
                <div className="bg-gray-800 rounded-lg p-4 text-center">
                  <Video className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                  <p className="text-sm text-white">YouTube Video</p>
                  <p className="text-xs text-gray-400 truncate mt-1">{value}</p>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <Video className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 truncate">{value}</p>
                </div>
              )
            ) : (
              <img src={value} alt="Preview" className="w-full rounded-lg max-h-48 object-contain" />
            )}
            <button
              onClick={() => onChange('')}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div 
            onClick={() => !uploading && inputRef.current?.click()}
            className={`text-center py-8 ${uploading ? 'cursor-wait' : 'cursor-pointer'}`}
          >
            {uploading ? (
              <div className="space-y-3">
                <div className="w-12 h-12 mx-auto relative">
                  <div className="animate-spin w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full"></div>
                </div>
                <div className="w-48 mx-auto">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Processing... {progress}%</p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Click to upload {isVideo ? 'video' : 'image'}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {isVideo ? 'MP4, WebM up to 100MB (YouTube URL recommended for large videos)' : 'JPG, PNG up to 10MB'}
                </p>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded-lg">
          <X className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
      {/* URL Input as alternative */}
      <div className="flex gap-2 items-center">
        <span className="text-xs text-gray-400">or paste URL:</span>
        <Input
          placeholder={isVideo ? "YouTube URL or direct video link" : "Image URL"}
          value={value?.startsWith('data:') ? '' : (value || '')}
          onChange={(e) => {
            setError('');
            onChange(e.target.value);
          }}
          className="flex-1 text-sm"
        />
      </div>
      {isVideo && (
        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
          💡 Tip: For better performance, consider uploading your video to YouTube and pasting the URL here.
        </p>
      )}
    </div>
  );
};

const AdminPhotoBoothPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  
  // Page content state
  const [content, setContent] = useState({
    hero: {
      title: 'Portrait Chocolate',
      subtitle: 'Live Photo Booth',
      tagline: 'The Most Delicious Photo Booth Experience in Hawaii!',
      description: 'Take fun photos and get them printed on premium chocolate! Perfect for weddings, parties, corporate events & more.',
      button_text: 'Book Now',
      button_link: '/contact',
      background_video: '',
      background_image: '',
      hero_videos: []
    },
    features: [
      { title: 'Flexible', description: 'We offer a variety of solutions to accommodate your party needs.', icon: 'flexible' },
      { title: 'Affordable', description: 'Cutting edge and friendly service at competitive prices.', icon: 'affordable' },
      { title: 'Quality', description: 'Top quality services with attention to cleanliness and appearance.', icon: 'quality' }
    ],
    commitment: {
      title: 'Our Commitment to Excellence!',
      description: 'At Portrait Chocolate, we are dedicated to providing the highest level of customer service and satisfaction.'
    },
    stats: [
      { value: 100, label: 'Client Satisfaction', suffix: '%' },
      { value: 5, label: 'Star Rating', suffix: '' },
      { value: 100, label: 'Customizable Templates', suffix: '%' }
    ],
    includes: [
      { icon: 'camera', title: 'Professional Camera Setup', description: 'High-quality DSLR camera with perfect lighting' },
      { icon: 'props', title: 'Fun Props & Accessories', description: 'Hats, glasses, signs, boas, and more!' },
      { icon: 'backdrop', title: 'Choice of Backdrops', description: 'Select from our beautiful backdrops' },
      { icon: 'chocolate', title: 'Chocolate Photo Prints', description: 'Photos printed on premium chocolate!' },
      { icon: 'share', title: 'Digital Sharing', description: 'Instantly share photos via text' },
      { icon: 'attendant', title: 'Friendly Attendant', description: 'Professional staff on-site' }
    ],
    gallery: [],
    backdrops: [],
    packages: [
      {
        name: 'Digital Booth',
        price: '375',
        duration: '2 Hours',
        features: ['Unlimited Digital Captures', 'Digital Booth', 'All props included'],
        note: '',
        popular: false
      },
      {
        name: 'Premium Booth',
        price: '575',
        duration: '2 Hours',
        features: ['Unlimited Prints', 'Digital Booth', '50 chocolate prints'],
        note: 'Additional prints: $3 each',
        popular: true
      }
    ],
    events: [
      { emoji: '💒', label: 'Weddings' },
      { emoji: '🎂', label: 'Birthdays' },
      { emoji: '🎓', label: 'Graduations' },
      { emoji: '🏢', label: 'Corporate' },
      { emoji: '🎉', label: 'Parties' },
      { emoji: '🌺', label: 'Luaus' }
    ],
    contact: {
      email: 'potraitchocolate@gmail.com',
      phone: '(808) 756-2017',
      location: 'Big Island, Hawaii',
      travel_note: 'Travel fee: $20 per hour away from Kailua-Kona'
    },
    videos: [],
    discount: {
      enabled: true,
      title: 'Special Offer!',
      subtitle: 'Book Now & Save',
      code: 'ALOHA15',
      discount_text: '15% OFF',
      description: 'Use code at checkout for your first booking!',
      expiry: 'Limited time offer'
    }
  });

  // Editing states
  const [editingPackage, setEditingPackage] = useState(null);
  const [newFeature, setNewFeature] = useState('');

  // Fetch existing content
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API}/admin/photobooth-page`);
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setContent(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.log('Using default content');
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      // Check content size (MongoDB has 16MB limit)
      const contentStr = JSON.stringify(content);
      const sizeInMB = new Blob([contentStr]).size / (1024 * 1024);
      
      if (sizeInMB > 15) {
        alert(`Content is too large (${sizeInMB.toFixed(1)}MB). MongoDB has a 16MB limit.\n\nTip: Use YouTube URLs for videos instead of uploading directly.`);
        setSaving(false);
        return;
      }
      
      console.log(`Saving content... Size: ${sizeInMB.toFixed(2)}MB`);
      
      const response = await fetch(`${API}/admin/photobooth-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: contentStr
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Save result:', result);
        alert('✅ Content saved successfully!');
      } else {
        const errorText = await response.text();
        console.error('Save failed:', response.status, errorText);
        alert(`Failed to save content: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      alert(`Error saving content: ${error.message}\n\nIf you uploaded large videos, try using YouTube URLs instead.`);
    } finally {
      setSaving(false);
    }
  };

  // Hero updates
  const updateHero = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  // Add hero video with file upload
  const addHeroVideo = (videoData) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        hero_videos: [...(prev.hero.hero_videos || []), videoData]
      }
    }));
  };

  const removeHeroVideo = (index) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        hero_videos: (prev.hero.hero_videos || []).filter((_, i) => i !== index)
      }
    }));
  };

  const updateHeroVideo = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        hero_videos: (prev.hero.hero_videos || []).map((v, i) => 
          i === index ? { ...v, [field]: value } : v
        )
      }
    }));
  };

  // Gallery
  const addGalleryImage = (imageData) => {
    setContent(prev => ({
      ...prev,
      gallery: [...(prev.gallery || []), imageData]
    }));
  };

  const removeGalleryImage = (index) => {
    setContent(prev => ({
      ...prev,
      gallery: (prev.gallery || []).filter((_, i) => i !== index)
    }));
  };

  const updateGalleryImage = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      gallery: (prev.gallery || []).map((img, i) => 
        i === index ? { ...img, [field]: value } : img
      )
    }));
  };

  // Backdrops
  const addBackdrop = (backdropData) => {
    setContent(prev => ({
      ...prev,
      backdrops: [...(prev.backdrops || []), backdropData]
    }));
  };

  const removeBackdrop = (index) => {
    setContent(prev => ({
      ...prev,
      backdrops: (prev.backdrops || []).filter((_, i) => i !== index)
    }));
  };

  const updateBackdrop = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      backdrops: (prev.backdrops || []).map((b, i) => 
        i === index ? { ...b, [field]: value } : b
      )
    }));
  };

  // Packages
  const updatePackage = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === index ? { ...pkg, [field]: value } : pkg
      )
    }));
  };

  const addPackage = () => {
    setContent(prev => ({
      ...prev,
      packages: [...prev.packages, {
        name: 'New Package',
        price: '0',
        duration: '2 Hours',
        features: ['Feature 1'],
        note: '',
        popular: false
      }]
    }));
  };

  const removePackage = (index) => {
    setContent(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const addPackageFeature = (pkgIndex) => {
    if (!newFeature.trim()) return;
    setContent(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === pkgIndex ? { ...pkg, features: [...pkg.features, newFeature.trim()] } : pkg
      )
    }));
    setNewFeature('');
  };

  const removePackageFeature = (pkgIndex, featureIndex) => {
    setContent(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === pkgIndex ? { ...pkg, features: pkg.features.filter((_, fi) => fi !== featureIndex) } : pkg
      )
    }));
  };

  // Contact
  const updateContact = (field, value) => {
    setContent(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  // Discount
  const updateDiscount = (field, value) => {
    setContent(prev => ({
      ...prev,
      discount: { ...(prev.discount || {}), [field]: value }
    }));
  };

  // Commitment
  const updateCommitment = (field, value) => {
    setContent(prev => ({
      ...prev,
      commitment: { ...(prev.commitment || {}), [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { id: 'hero', label: 'Hero Section', icon: '🎬' },
    { id: 'videos', label: 'Hero Videos', icon: '📹' },
    { id: 'features', label: 'Features', icon: '✨' },
    { id: 'commitment', label: 'Commitment', icon: '🎯' },
    { id: 'gallery', label: 'Gallery', icon: '🖼️' },
    { id: 'backdrops', label: 'Backdrops', icon: '🎨' },
    { id: 'packages', label: 'Packages', icon: '💰' },
    { id: 'discount', label: 'Discount Popup', icon: '🎁' },
    { id: 'contact', label: 'Contact', icon: '📞' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">Live Photo Booth Page Editor</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Content size indicator */}
            {(() => {
              const sizeInMB = new Blob([JSON.stringify(content)]).size / (1024 * 1024);
              const isLarge = sizeInMB > 10;
              const isCritical = sizeInMB > 14;
              return (
                <span className={`text-xs px-2 py-1 rounded-full ${isCritical ? 'bg-red-100 text-red-700' : isLarge ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                  {sizeInMB.toFixed(1)}MB / 16MB
                </span>
              );
            })()}
            <Button 
              variant="outline" 
              onClick={() => window.open('/live-photobooth', '_blank')}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button 
              onClick={saveContent}
              disabled={saving}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="space-y-1 p-2">
                  {sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                        activeSection === section.id 
                          ? 'bg-amber-100 text-amber-800 font-medium' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xl">{section.icon}</span>
                      <span>{section.label}</span>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Hero Section */}
            {activeSection === 'hero' && (
              <Card>
                <CardHeader>
                  <CardTitle>Hero Section</CardTitle>
                  <CardDescription>Main banner at the top of the page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input 
                        value={content.hero.title} 
                        onChange={(e) => updateHero('title', e.target.value)}
                        placeholder="Portrait Chocolate"
                      />
                    </div>
                    <div>
                      <Label>Subtitle</Label>
                      <Input 
                        value={content.hero.subtitle} 
                        onChange={(e) => updateHero('subtitle', e.target.value)}
                        placeholder="Live Photo Booth"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Tagline</Label>
                    <Input 
                      value={content.hero.tagline} 
                      onChange={(e) => updateHero('tagline', e.target.value)}
                      placeholder="The Most Delicious Photo Booth Experience!"
                    />
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={content.hero.description} 
                      onChange={(e) => updateHero('description', e.target.value)}
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Button Text</Label>
                      <Input 
                        value={content.hero.button_text} 
                        onChange={(e) => updateHero('button_text', e.target.value)}
                        placeholder="Book Now"
                      />
                    </div>
                    <div>
                      <Label>Button Link</Label>
                      <Input 
                        value={content.hero.button_link} 
                        onChange={(e) => updateHero('button_link', e.target.value)}
                        placeholder="/contact"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h4 className="font-medium mb-4">Background Media</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <ImageUploader
                        label="Background Image"
                        value={content.hero.background_image}
                        onChange={(val) => updateHero('background_image', val)}
                        accept="image/*"
                      />
                      <ImageUploader
                        label="Background Video (overrides image)"
                        value={content.hero.background_video}
                        onChange={(val) => updateHero('background_video', val)}
                        accept="video/*"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hero Videos Section */}
            {activeSection === 'videos' && (
              <Card>
                <CardHeader>
                  <CardTitle>Hero Videos</CardTitle>
                  <CardDescription>Videos displayed below the hero banner. Upload videos and choose custom thumbnails.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {(content.hero.hero_videos || []).map((video, index) => (
                    <Card key={index} className="p-6 bg-gray-50 border-2 border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                            {index + 1}
                          </div>
                          <h4 className="font-semibold text-lg">{video.title || `Video ${index + 1}`}</h4>
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => removeHeroVideo(index)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <Label className="text-base font-medium">Video Title</Label>
                          <Input 
                            value={video.title || ''} 
                            onChange={(e) => updateHeroVideo(index, 'title', e.target.value)}
                            placeholder="Enter video title"
                            className="mt-1"
                          />
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Video Upload */}
                          <div>
                            <ImageUploader
                              label="Video File or URL"
                              value={video.url}
                              onChange={(val) => updateHeroVideo(index, 'url', val)}
                              accept="video/*"
                            />
                          </div>
                          
                          {/* Thumbnail Upload */}
                          <div>
                            <ImageUploader
                              label="Video Thumbnail (optional)"
                              value={video.thumbnail}
                              onChange={(val) => updateHeroVideo(index, 'thumbnail', val)}
                              accept="image/*"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              Upload a custom thumbnail image that shows before video plays
                            </p>
                          </div>
                        </div>
                        
                        {/* Preview */}
                        {video.url && (
                          <div className="border-t pt-4">
                            <Label className="text-sm text-gray-600 mb-2 block">Preview</Label>
                            <div className="bg-black rounded-xl overflow-hidden max-w-md">
                              {video.url.includes('youtube') || video.url.includes('youtu.be') ? (
                                <div className="aspect-video flex items-center justify-center bg-gray-800 text-white">
                                  <div className="text-center">
                                    <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm opacity-70">YouTube Video</p>
                                  </div>
                                </div>
                              ) : (
                                <video 
                                  controls 
                                  className="w-full aspect-video"
                                  poster={video.thumbnail || ''}
                                >
                                  <source src={video.url} type="video/mp4" />
                                </video>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                  
                  <Button 
                    variant="outline" 
                    onClick={() => addHeroVideo({ url: '', title: 'New Video', thumbnail: '' })}
                    className="w-full py-6 border-2 border-dashed border-amber-300 hover:border-amber-500 hover:bg-amber-50"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Video
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Features Section */}
            {activeSection === 'features' && (
              <Card>
                <CardHeader>
                  <CardTitle>Features Section</CardTitle>
                  <CardDescription>3 key features displayed prominently</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(content.features || []).map((feature, index) => (
                    <Card key={index} className="p-4 bg-gray-50">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Title</Label>
                          <Input 
                            value={feature.title} 
                            onChange={(e) => {
                              const newFeatures = [...content.features];
                              newFeatures[index].title = e.target.value;
                              setContent(prev => ({ ...prev, features: newFeatures }));
                            }}
                          />
                        </div>
                        <div>
                          <Label>Icon</Label>
                          <select 
                            value={feature.icon}
                            onChange={(e) => {
                              const newFeatures = [...content.features];
                              newFeatures[index].icon = e.target.value;
                              setContent(prev => ({ ...prev, features: newFeatures }));
                            }}
                            className="w-full rounded-md border border-gray-200 px-3 py-2"
                          >
                            <option value="flexible">Flexible ✨</option>
                            <option value="affordable">Affordable ⭐</option>
                            <option value="quality">Quality 🏆</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label>Description</Label>
                        <Textarea 
                          value={feature.description} 
                          onChange={(e) => {
                            const newFeatures = [...content.features];
                            newFeatures[index].description = e.target.value;
                            setContent(prev => ({ ...prev, features: newFeatures }));
                          }}
                          rows={2}
                        />
                      </div>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Commitment Section */}
            {activeSection === 'commitment' && (
              <Card>
                <CardHeader>
                  <CardTitle>Commitment Section</CardTitle>
                  <CardDescription>Your commitment statement and stats</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={content.commitment?.title || ''} 
                      onChange={(e) => updateCommitment('title', e.target.value)}
                      placeholder="Our Commitment to Excellence!"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={content.commitment?.description || ''} 
                      onChange={(e) => updateCommitment('description', e.target.value)}
                      rows={4}
                    />
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-4">Stats (Animated Numbers)</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      {(content.stats || []).map((stat, index) => (
                        <Card key={index} className="p-4 bg-gray-50">
                          <div className="space-y-2">
                            <div>
                              <Label>Value</Label>
                              <Input 
                                type="number"
                                value={stat.value} 
                                onChange={(e) => {
                                  const newStats = [...content.stats];
                                  newStats[index].value = parseInt(e.target.value) || 0;
                                  setContent(prev => ({ ...prev, stats: newStats }));
                                }}
                              />
                            </div>
                            <div>
                              <Label>Suffix (%, etc)</Label>
                              <Input 
                                value={stat.suffix} 
                                onChange={(e) => {
                                  const newStats = [...content.stats];
                                  newStats[index].suffix = e.target.value;
                                  setContent(prev => ({ ...prev, stats: newStats }));
                                }}
                              />
                            </div>
                            <div>
                              <Label>Label</Label>
                              <Input 
                                value={stat.label} 
                                onChange={(e) => {
                                  const newStats = [...content.stats];
                                  newStats[index].label = e.target.value;
                                  setContent(prev => ({ ...prev, stats: newStats }));
                                }}
                              />
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gallery Section */}
            {activeSection === 'gallery' && (
              <Card>
                <CardHeader>
                  <CardTitle>Photo Gallery</CardTitle>
                  <CardDescription>Upload photos to showcase your work</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {(content.gallery || []).map((image, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={image.url} 
                          alt={image.alt || 'Gallery'} 
                          className="w-full aspect-square object-cover rounded-xl"
                        />
                        <button
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <Card className="p-4 bg-gray-50">
                    <h4 className="font-medium mb-4">Add New Image</h4>
                    <ImageUploader
                      value=""
                      onChange={(val) => {
                        if (val) addGalleryImage({ url: val, alt: 'Gallery photo' });
                      }}
                      accept="image/*"
                    />
                  </Card>
                </CardContent>
              </Card>
            )}

            {/* Backdrops Section */}
            {activeSection === 'backdrops' && (
              <Card>
                <CardHeader>
                  <CardTitle>Backdrops</CardTitle>
                  <CardDescription>Available backdrop options for customers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(content.backdrops || []).map((backdrop, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={backdrop.image} 
                          alt={backdrop.name} 
                          className="w-full aspect-[3/2] object-cover rounded-xl"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white p-2 rounded-b-xl">
                          <Input 
                            value={backdrop.name}
                            onChange={(e) => updateBackdrop(index, 'name', e.target.value)}
                            className="bg-transparent border-0 text-white text-sm text-center p-0 h-auto"
                          />
                        </div>
                        <button
                          onClick={() => removeBackdrop(index)}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <Card className="p-4 bg-gray-50">
                    <h4 className="font-medium mb-4">Add New Backdrop</h4>
                    <div className="space-y-4">
                      <div>
                        <Label>Backdrop Name</Label>
                        <Input 
                          id="new-backdrop-name"
                          placeholder="e.g., Tropical Paradise"
                        />
                      </div>
                      <ImageUploader
                        label="Backdrop Image"
                        value=""
                        onChange={(val) => {
                          const nameInput = document.getElementById('new-backdrop-name');
                          const name = nameInput?.value || 'New Backdrop';
                          if (val) {
                            addBackdrop({ name, image: val });
                            if (nameInput) nameInput.value = '';
                          }
                        }}
                        accept="image/*"
                      />
                    </div>
                  </Card>
                </CardContent>
              </Card>
            )}

            {/* Packages Section */}
            {activeSection === 'packages' && (
              <Card>
                <CardHeader>
                  <CardTitle>Pricing Packages</CardTitle>
                  <CardDescription>Manage your service packages</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {content.packages.map((pkg, index) => (
                    <Card key={index} className={`p-6 ${pkg.popular ? 'border-amber-400 border-2' : ''}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <h4 className="font-bold text-lg">{pkg.name}</h4>
                          {pkg.popular && (
                            <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full">
                              POPULAR
                            </span>
                          )}
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => removePackage(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label>Package Name</Label>
                          <Input 
                            value={pkg.name} 
                            onChange={(e) => updatePackage(index, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Price ($)</Label>
                          <Input 
                            value={pkg.price} 
                            onChange={(e) => updatePackage(index, 'price', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Duration</Label>
                          <Input 
                            value={pkg.duration} 
                            onChange={(e) => updatePackage(index, 'duration', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <Label>Features</Label>
                        <div className="space-y-2 mt-2">
                          {pkg.features.map((feature, fi) => (
                            <div key={fi} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" />
                              <Input 
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...pkg.features];
                                  newFeatures[fi] = e.target.value;
                                  updatePackage(index, 'features', newFeatures);
                                }}
                                className="flex-1"
                              />
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removePackageFeature(index, fi)}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input 
                              placeholder="Add new feature..."
                              value={editingPackage === index ? newFeature : ''}
                              onChange={(e) => {
                                setEditingPackage(index);
                                setNewFeature(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  addPackageFeature(index);
                                }
                              }}
                            />
                            <Button 
                              variant="outline"
                              onClick={() => addPackageFeature(index)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Note (optional)</Label>
                          <Input 
                            value={pkg.note || ''} 
                            onChange={(e) => updatePackage(index, 'note', e.target.value)}
                            placeholder="e.g., Additional prints: $3 each"
                          />
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                          <Switch 
                            checked={pkg.popular}
                            onCheckedChange={(checked) => updatePackage(index, 'popular', checked)}
                          />
                          <Label>Mark as Popular</Label>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <Button 
                    variant="outline" 
                    onClick={addPackage}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Package
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Discount Popup Section */}
            {activeSection === 'discount' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Discount Popup
                  </CardTitle>
                  <CardDescription>Configure the promotional popup that appears to visitors</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={content.discount?.enabled || false}
                      onCheckedChange={(checked) => updateDiscount('enabled', checked)}
                    />
                    <Label>Enable Discount Popup</Label>
                  </div>
                  
                  {content.discount?.enabled && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Title</Label>
                          <Input 
                            value={content.discount?.title || ''} 
                            onChange={(e) => updateDiscount('title', e.target.value)}
                            placeholder="Special Offer!"
                          />
                        </div>
                        <div>
                          <Label>Subtitle</Label>
                          <Input 
                            value={content.discount?.subtitle || ''} 
                            onChange={(e) => updateDiscount('subtitle', e.target.value)}
                            placeholder="Book Now & Save"
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Discount Text (shown prominently)</Label>
                          <Input 
                            value={content.discount?.discount_text || ''} 
                            onChange={(e) => updateDiscount('discount_text', e.target.value)}
                            placeholder="15% OFF"
                          />
                        </div>
                        <div>
                          <Label>Coupon Code</Label>
                          <Input 
                            value={content.discount?.code || ''} 
                            onChange={(e) => updateDiscount('code', e.target.value.toUpperCase())}
                            placeholder="ALOHA15"
                            className="font-mono"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Description</Label>
                        <Input 
                          value={content.discount?.description || ''} 
                          onChange={(e) => updateDiscount('description', e.target.value)}
                          placeholder="Use code at checkout for your first booking!"
                        />
                      </div>
                      
                      <div>
                        <Label>Expiry Note</Label>
                        <Input 
                          value={content.discount?.expiry || ''} 
                          onChange={(e) => updateDiscount('expiry', e.target.value)}
                          placeholder="Limited time offer"
                        />
                      </div>
                      
                      {/* Preview */}
                      <div className="border-t pt-6">
                        <h4 className="font-medium mb-4">Preview</h4>
                        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-1 max-w-sm mx-auto">
                          <div className="bg-white rounded-2xl p-6 text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-1">{content.discount?.title || 'Special Offer!'}</h3>
                            <p className="text-gray-600 text-sm mb-3">{content.discount?.subtitle || 'Book Now & Save'}</p>
                            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-2xl font-bold py-2 px-4 rounded-xl mb-3">
                              {content.discount?.discount_text || '15% OFF'}
                            </div>
                            <div className="bg-gray-100 rounded-lg p-2 mb-3">
                              <span className="font-mono font-bold text-amber-600">{content.discount?.code || 'ALOHA15'}</span>
                            </div>
                            <p className="text-xs text-gray-500">{content.discount?.expiry || 'Limited time offer'}</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contact Section */}
            {activeSection === 'contact' && (
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>Your business contact details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input 
                        value={content.contact.email} 
                        onChange={(e) => updateContact('email', e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        value={content.contact.phone} 
                        onChange={(e) => updateContact('phone', e.target.value)}
                        placeholder="(808) 756-2017"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Location</Label>
                    <Input 
                      value={content.contact.location} 
                      onChange={(e) => updateContact('location', e.target.value)}
                      placeholder="Big Island, Hawaii"
                    />
                  </div>
                  
                  <div>
                    <Label>Travel Fee Note</Label>
                    <Input 
                      value={content.contact.travel_note} 
                      onChange={(e) => updateContact('travel_note', e.target.value)}
                      placeholder="Travel fee: $20 per hour away from Kailua-Kona"
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPhotoBoothPage;
